import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { Howler } from "howler";
import {
  selectAutoPlayActive,
  selectFreeSpinsActive,
} from "../State/selectors";

export const AppActivityContext = createContext(null);

export const AppActivityProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(true);
  const [shouldShakePlay, setShouldShakePlay] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);
  const lastSpinTimeRef = useRef(null);

  // Get autoplay and free spin states from Redux
  const { autoPlayActive } = useSelector(selectAutoPlayActive);
  const { freeSpinsActive } = useSelector(selectFreeSpinsActive);

  // Helper function to compute activity state
  const computeActive = () => {
    // Visible is the primary signal; hidden covers older browsers
    return document.visibilityState === "visible" && !document.hidden;
  };

  // Set up event listeners for activity detection
  useEffect(() => {
    const apply = () => {
      const active = computeActive();

      setIsActive((prevActive) => {
        if (prevActive === active) {
          return prevActive; // No change
        }

        // Audio + Pixi ticker control
        Howler.mute(!active);
        const app = globalThis.__PIXI_APP__;
        if (app && app.ticker) {
          if (active) {
            app.ticker.start();
          } else {
            app.ticker.stop();
          }
        }

        return active;
      });
    };

    // Initialize with current state
    apply();

    const events = [
      "visibilitychange",
      "pagehide",
      "freeze",
      "blur",
      "focus",
      "pageshow",
    ];

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, apply, { passive: true });
    });

    // Cleanup function
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, apply, { passive: true });
      });
    };
  }, []); // Remove isActive dependency

  // Simple idle detection for play button shaking
  useEffect(() => {
    const IDLE_THRESHOLD = 10000; // 30 seconds
    const SHAKE_DURATION = 2000; // 2 seconds of shaking
    let shakeTimeout = null;

    const checkIdleAndShake = () => {
      // Don't shake if tab is inactive, timer not started, autoplay is active, or free spins are active
      if (
        !isActive ||
        !timerStarted ||
        !lastSpinTimeRef.current ||
        autoPlayActive ||
        freeSpinsActive
      ) {
        return;
      }

      const timeSinceLastSpin = Date.now() - lastSpinTimeRef.current;

      if (timeSinceLastSpin >= IDLE_THRESHOLD && !shouldShakePlay) {
        setShouldShakePlay(true);

        // Stop shaking after duration
        shakeTimeout = setTimeout(() => {
          setShouldShakePlay(false);
        }, SHAKE_DURATION);
      }
    };

    // Check every 10 seconds
    const interval = setInterval(checkIdleAndShake, 5000);

    return () => {
      clearInterval(interval);
      if (shakeTimeout) {
        clearTimeout(shakeTimeout);
      }
    };
  }, [
    isActive,
    shouldShakePlay,
    timerStarted,
    autoPlayActive,
    freeSpinsActive,
  ]);

  // Function to start the idle timer (called when intro popup closes)
  const startIdleTimer = useCallback(() => {
    if (!timerStarted) {
      lastSpinTimeRef.current = Date.now();
      setTimerStarted(true);
    }
  }, [timerStarted]);

  // Function to reset spin timer (called from ButtonPlay)
  const resetSpinTimer = useCallback(() => {
    if (timerStarted) {
      lastSpinTimeRef.current = Date.now();
      setShouldShakePlay(false);
    }
  }, [timerStarted]);

  // Reset timer when autoplay ends to start fresh idle detection
  useEffect(() => {
    if (!autoPlayActive && timerStarted) {
      lastSpinTimeRef.current = Date.now();
      setShouldShakePlay(false);
    }
  }, [autoPlayActive, timerStarted]);

  // Reset timer when free spins end to start fresh idle detection
  useEffect(() => {
    if (!freeSpinsActive && timerStarted) {
      lastSpinTimeRef.current = Date.now();
      setShouldShakePlay(false);
    }
  }, [freeSpinsActive, timerStarted]);

  // Stable context value
  const value = useMemo(
    () => ({
      isActive,
      shouldShakePlay,
      resetSpinTimer,
      startIdleTimer,
    }),
    [isActive, shouldShakePlay, resetSpinTimer, startIdleTimer]
  );

  return (
    <AppActivityContext.Provider value={value}>
      {children}
    </AppActivityContext.Provider>
  );
};

// Hook for consuming the context
export const useAppActivity = () => {
  const context = useContext(AppActivityContext);
  if (!context) {
    throw new Error(
      "useAppActivity must be used within an AppActivityProvider"
    );
  }
  return context;
};
